import { Grid, LinearProgress } from '@mui/material';
import { useEffect } from 'react';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import SatisfactionSurveySection from '../satisfaction-survey-settings/SatisfactionSurveySection';
import { useBypassOrderOnApprovalAlertModal } from './hooks/useBypassOrderOnApprovalAlertModal';
import BypassOrderOnApprovalAlertModal from './modals/BypassOrderOnApprovalAlertModal';
import CatalogsSectionGeneralSettings from './sections/components/CatalogsSectionGeneralSettings';
import ConnectionsSectionGeneralSettings from './sections/components/ConnectionsSectionGeneralSettings';
import CopyOrderSectionGeneralSettings from './sections/components/CopyOrderSectionGeneralSettings';
import CustomerSupportSectionGeneralSettings from './sections/components/CustomerSupportSectionGeneralSettings';
import DropboxSectionGeneralSettings from './sections/components/DropboxSectionGeneralSettings';
import GridSizeSectionGeneralSettings from './sections/components/GridSizeSectionGeneralSettings';
import MediasSectionGeneralSettings from './sections/components/MediasSectionGeneralSettings';
import MinimumOrderSectionGeneralSettings from './sections/components/MinimumOrderSectionGeneralSettings';
import ObservationSectionGeneralSettings from './sections/components/ObservationSectionGeneralSettings/ObservationSectionGeneralSettings';
import OrderCollectionSectionGeneralSettings from './sections/components/OrderCollectionSectionGeneralSettings';
import OrderContractSectionGeneralSettings from './sections/components/OrderContractSectionGeneralSettings';
import OrderDivisionSectionGeneralSettings from './sections/components/OrderDivisionSectionGeneralSettings';
import OrderPdfConfigSectionGeneralSettings from './sections/components/OrderPdfConfigSectionGeneralSettings';
import OrderSectionGeneralSettings from './sections/components/OrderSectionGeneralSettings';
import PaymentGatewaySectionGeneralSettings from './sections/components/PaymentGatewaySectionGeneralSettings';
import PaymentMethodSectionGeneralSettings from './sections/components/PaymentMethodSectionGeneralSettings';
import PriceSettingsSectionGeneralSettings from './sections/components/PriceSettingsSectionGeneralSettings';
import ProductInputLockSectionGeneralSettings from './sections/components/ProductInputLockSectionGeneralSettings';
import ProductsAccessGeneralSettings from './sections/components/ProductsAccessGeneralSettings';
import PublicShowroomSectionGeneralSettings from './sections/components/PublicShowroomSectionGeneralSettings';
import SalesRepresentativeSectionGeneralSettings from './sections/components/SalesRepresentativeSectionGeneralSettings';
import StockSectionGeneralSettings from './sections/components/StockSectionGeneralSettings';
import TimezoneSectionGeneralSettings from './sections/components/TimezoneSectionGeneralSettings';
import OrderBillingForecastSection from './sections/components/orderBillingForecast/OrderBillingForecastSection';
import OrderInactivitySectionGeneralSettings from './sections/components/orderInactivity/OrderInactivitySectionGeneralSettings';
import RelatedProductsSection from './sections/components/relatedProducts/RelatedProductsSection';
import SelloutSectionGeneralSettings from './sections/components/sellout/SelloutSectionGeneralSettings';
import useGeneralSettings from './useGeneralSettings';

const GeneralSettingsForm = () => {
  const { isEnableSatisfactionSurvey } = useFeatureFlag();
  const {
    isLoading,
    isLoadingContract,
    contract,
    link,
    generalSettings,
    selectedSplitSettings,
    splitSettings,
    orderSeparation,
    fetchGeneralSettings,
    getSettingsPage,
    handleChangeAndBlur,
    handleChangeAndBlurList,
    handleChangeSplitSettings,
    handleChange,
    handleBlur,
    handleDeleteFile,
    handleChangeFile,
  } = useGeneralSettings();

  const {
    handleBypassOrderOnApprovalAlert,
    handleChangeBypassOrderOnApproval,
    isOpen: isBypassOrderOnApprovalAlertOpen,
    closeModal: closeBypassOrderOnApprovalAlert,
  } = useBypassOrderOnApprovalAlertModal({
    handleChangeAndBlur,
  });

  useEffect(() => {
    getSettingsPage();
  }, [getSettingsPage]);

  if (isLoading) {
    return <LinearProgress />;
  }

  return (
    <>
      <Grid container spacing={4} sx={{ alignItems: 'flex-start' }}>
        <Grid size={{ sm: 12, md: 6 }}>
          <OrderDivisionSectionGeneralSettings
            selectedSplitSettings={selectedSplitSettings}
            handleChangeSplitSettings={handleChangeSplitSettings}
            splitSettings={splitSettings}
          />
        </Grid>
        <Grid size={{ sm: 12, md: 6 }}>
          <MinimumOrderSectionGeneralSettings
            generalSettings={generalSettings}
            orderSeparation={orderSeparation}
            handleChange={handleChange}
            handleBlur={handleBlur}
          />
        </Grid>
        <Grid size={{ sm: 12, md: 6 }}>
          <OrderInactivitySectionGeneralSettings
            handleChange={handleChange}
            handleBlur={handleBlur}
            generalSettings={generalSettings}
            handleChangeAndBlur={handleChangeAndBlur}
          />
        </Grid>
        <Grid size={{ sm: 12, md: 6 }}>
          <OrderContractSectionGeneralSettings
            generalSettings={generalSettings}
            contract={contract}
            link={link}
            isLoadingContract={isLoadingContract}
            handleChangeAndBlur={handleChangeAndBlur}
            handleChangeFile={handleChangeFile}
            handleDeleteFile={handleDeleteFile}
          />
        </Grid>
        <Grid size={{ sm: 12, md: 6 }}>
          <StockSectionGeneralSettings
            generalSettings={generalSettings}
            handleChangeAndBlur={handleChangeAndBlur}
            handleBlur={handleBlur}
            handleChange={handleChange}
          />
        </Grid>
        <Grid size={{ sm: 12, md: 6 }}>
          <ObservationSectionGeneralSettings
            generalSettings={generalSettings}
            handleChangeAndBlur={handleChangeAndBlur}
          />
        </Grid>
        <Grid size={{ sm: 12, md: 6 }}>
          <OrderPdfConfigSectionGeneralSettings
            generalSettings={generalSettings}
            handleChangeAndBlur={handleChangeAndBlur}
            handleChangeAndBlurList={handleChangeAndBlurList}
          />
        </Grid>
        <Grid size={{ sm: 12, md: 6 }}>
          <PriceSettingsSectionGeneralSettings
            generalSettings={generalSettings}
            handleChangeAndBlur={handleChangeAndBlur}
          />
        </Grid>
        <Grid size={{ sm: 12, md: 6 }}>
          <PublicShowroomSectionGeneralSettings
            generalSettings={generalSettings}
            handleChangeAndBlur={handleChangeAndBlur}
          />
        </Grid>
        <Grid size={{ sm: 12, md: 6 }}>
          <SalesRepresentativeSectionGeneralSettings
            generalSettings={generalSettings}
            handleChangeAndBlur={handleChangeAndBlur}
          />
        </Grid>
        <Grid size={{ sm: 12, md: 6 }}>
          <ConnectionsSectionGeneralSettings
            generalSettings={generalSettings}
            handleChangeAndBlur={handleChangeAndBlur}
          />
        </Grid>
        <Grid size={{ sm: 12, md: 6 }}>
          <TimezoneSectionGeneralSettings
            generalSettings={generalSettings}
            handleChangeAndBlur={handleChangeAndBlur}
          />
        </Grid>
        <Grid size={{ sm: 12, md: 6 }}>
          <CustomerSupportSectionGeneralSettings
            generalSettings={generalSettings}
            handleChange={handleChange}
            handleBlur={handleBlur}
          />
        </Grid>
        <Grid size={{ sm: 12, md: 6 }}>
          <RelatedProductsSection
            generalSettings={generalSettings}
            handleChangeAndBlur={handleChangeAndBlur}
          />
        </Grid>
        <Grid size={{ sm: 12, md: 6 }}>
          <OrderCollectionSectionGeneralSettings
            generalSettings={generalSettings}
            orderSeparation={orderSeparation}
            handleChangeAndBlur={handleChangeAndBlur}
          />
        </Grid>
        <Grid size={{ sm: 12, md: 6 }}>
          <DropboxSectionGeneralSettings
            generalSettings={generalSettings}
            fetchGeneralSettings={fetchGeneralSettings}
          />
        </Grid>
        <Grid size={{ sm: 12, md: 6 }}>
          <MediasSectionGeneralSettings
            generalSettings={generalSettings}
            handleChangeAndBlur={handleChangeAndBlur}
          />
        </Grid>
        <Grid size={{ sm: 12, md: 6 }}>
          <CopyOrderSectionGeneralSettings
            generalSettings={generalSettings}
            handleChangeAndBlur={handleChangeAndBlur}
          />
        </Grid>
        <Grid size={{ sm: 12, md: 6 }}>
          <OrderSectionGeneralSettings
            generalSettings={generalSettings}
            handleChangeAndBlur={handleChangeAndBlur}
            handleBypassOrderOnApprovalAlert={handleBypassOrderOnApprovalAlert}
          />
        </Grid>
        <Grid size={{ sm: 12, md: 6 }}>
          <GridSizeSectionGeneralSettings
            generalSettings={generalSettings}
            handleChangeAndBlur={handleChangeAndBlur}
          />
        </Grid>
        <Grid size={{ sm: 12, md: 6 }}>
          <SelloutSectionGeneralSettings
            generalSettings={generalSettings}
            handleChange={handleChange}
            handleBlur={handleBlur}
            handleChangeAndBlur={handleChangeAndBlur}
            handleChangeAndBlurList={handleChangeAndBlurList}
          />
        </Grid>
        {isEnableSatisfactionSurvey && (
          <Grid size={{ sm: 12, md: 6 }}>
            <SatisfactionSurveySection />
          </Grid>
        )}
        <Grid size={{ sm: 12, md: 6 }}>
          <ProductInputLockSectionGeneralSettings
            generalSettings={generalSettings}
            handleChange={handleChange}
            handleBlur={handleBlur}
          />
        </Grid>
        <Grid size={{ sm: 12, md: 6 }}>
          <ProductsAccessGeneralSettings
            generalSettings={generalSettings}
            handleChangeAndBlur={handleChangeAndBlur}
            handleChangeAndBlurList={handleChangeAndBlurList}
          />
        </Grid>
        <Grid size={{ sm: 12, md: 6 }}>
          <CatalogsSectionGeneralSettings
            generalSettings={generalSettings}
            handleChangeAndBlur={handleChangeAndBlur}
          />
        </Grid>
        <Grid size={{ sm: 12, md: 6 }}>
          <PaymentMethodSectionGeneralSettings
            generalSettings={generalSettings}
            handleChangeAndBlur={handleChangeAndBlur}
          />
        </Grid>
        <Grid size={{ sm: 12, md: 6 }}>
          <PaymentGatewaySectionGeneralSettings
            generalSettings={generalSettings}
            handleChangeAndBlur={handleChangeAndBlur}
          />
        </Grid>
        <Grid size={{ sm: 12, md: 6 }}>
          <OrderBillingForecastSection
            generalSettings={generalSettings}
            handleChangeAndBlur={handleChangeAndBlur}
          />
        </Grid>
      </Grid>
      <BypassOrderOnApprovalAlertModal
        open={isBypassOrderOnApprovalAlertOpen}
        onAccept={() => handleChangeBypassOrderOnApproval(true)}
        onDeny={() => closeBypassOrderOnApprovalAlert()}
      />
    </>
  );
};

export default GeneralSettingsForm;
